import { BaseApiClient } from './base-client'
import { API_ROUTES, interpolateRoute } from '@super/shared-types'
import { withErrorHandling } from '../utils'
import type {
  EvalCase,
  EvalRun,
  EvalResultDetail,
  EvalCasesListResponse,
  EvalRunsListResponse,
  EvalResultsListResponse,
  EvalCostEstimateResponse,
  TriggerEvalRunResponse,
  ApiOffsetPaginatedResponse,
  ApiResponse,
} from '@super/shared-types'
import { EvalPromptType } from '@super/shared-types'

export interface CreateEvalCasePayload {
  name: string
  description?: string
  companyId?: string
  promptType: EvalPromptType
  content: string
  inputContext?: Record<string, unknown>
  expectedOutput: Record<string, unknown>
  sourceSessionId?: string
  sourceThreadId?: string
}

export interface UpdateEvalCasePayload {
  name?: string
  description?: string
  companyId?: string
  content?: string
  inputContext?: Record<string, unknown> | null
  expectedOutput?: Record<string, unknown>
}

export interface TriggerEvalRunPayload {
  companyId?: string
  promptType: EvalPromptType
}

export class EvalApiClient extends BaseApiClient {
  // ---------------------------------------------------------------------------
  // Cases
  // ---------------------------------------------------------------------------

  async getCases(opts: {
    companyId?: string
    promptType?: string
    limit?: number
    offset?: number
  }): Promise<EvalCasesListResponse | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiOffsetPaginatedResponse<EvalCase>
        >(`${API_ROUTES.ADMIN.EVAL.CASES}${this.buildQueryString(opts)}`)
        return response ?? null
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error fetching eval cases',
      },
    )
  }

  async createCase(payload: CreateEvalCasePayload): Promise<EvalCase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<EvalCase>>(
          API_ROUTES.ADMIN.EVAL.CASES,
          {
            method: 'POST',
            body: JSON.stringify(payload),
          },
        )
        return response?.data ?? null
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error creating eval case',
      },
    )
  }

  async getCaseById(id: string): Promise<EvalCase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<EvalCase>>(
          interpolateRoute(API_ROUTES.ADMIN.EVAL.CASE_BY_ID, { id }),
        )
        return response?.data ?? null
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error fetching eval case',
      },
    )
  }

  async updateCase(
    id: string,
    payload: UpdateEvalCasePayload,
  ): Promise<EvalCase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<EvalCase>>(
          interpolateRoute(API_ROUTES.ADMIN.EVAL.CASE_BY_ID, { id }),
          { method: 'PATCH', body: JSON.stringify(payload) },
        )
        return response?.data ?? null
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error updating eval case',
      },
    )
  }

  async deleteCase(id: string): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        await this.request<void>(
          interpolateRoute(API_ROUTES.ADMIN.EVAL.CASE_BY_ID, { id }),
          { method: 'DELETE' },
        )
        return true
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error deleting eval case',
      },
    )
    return result ?? false
  }

  // ---------------------------------------------------------------------------
  // Runs
  // ---------------------------------------------------------------------------

  async estimateCost(opts: {
    companyId?: string
    promptType?: string
  }): Promise<EvalCostEstimateResponse | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<EvalCostEstimateResponse>
        >(`${API_ROUTES.ADMIN.EVAL.RUN_ESTIMATE}${this.buildQueryString(opts)}`)
        return response?.data ?? null
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error fetching eval cost estimate',
      },
    )
  }

  async listRuns(opts: {
    companyId?: string
    limit?: number
    offset?: number
  }): Promise<EvalRunsListResponse | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiOffsetPaginatedResponse<EvalRun>
        >(`${API_ROUTES.ADMIN.EVAL.RUNS}${this.buildQueryString(opts)}`)
        return response ?? null
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error fetching eval runs',
      },
    )
  }

  async triggerRun(
    payload: TriggerEvalRunPayload,
  ): Promise<TriggerEvalRunResponse | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<TriggerEvalRunResponse>
        >(API_ROUTES.ADMIN.EVAL.RUNS, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
        return response?.data ?? null
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error triggering eval run',
      },
    )
  }

  async getRunById(id: string): Promise<EvalRun | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<EvalRun>>(
          interpolateRoute(API_ROUTES.ADMIN.EVAL.RUN_BY_ID, { id }),
        )
        return response?.data ?? null
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error fetching eval run',
      },
    )
  }

  // ---------------------------------------------------------------------------
  // Results
  // ---------------------------------------------------------------------------

  async getResultById(id: string): Promise<EvalResultDetail | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<EvalResultDetail>>(
          interpolateRoute(API_ROUTES.ADMIN.EVAL.RESULT_BY_ID, { id }),
        )
        return response?.data ?? null
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error fetching eval result',
      },
    )
  }

  async listResults(opts: {
    runId: string
    passed?: boolean
    limit?: number
    offset?: number
  }): Promise<EvalResultsListResponse | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<EvalResultsListResponse>(
          `${API_ROUTES.ADMIN.EVAL.RESULTS}${this.buildQueryString(opts)}`,
        )
        return response ?? null
      },
      {
        onApiError: 'log-and-return-null',
        errorContext: 'Error fetching eval results',
      },
    )
  }
}

export const evalApiClient = new EvalApiClient()
